import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

export class PasswordValidators {
  static required(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      return control.value ? null : { required: 'Password is required' };
    };
  }

  static allowedCharacters(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value ?? '';

      return /[^A-Za-z\d.,!@#$%^&*?(){}[\]|_<>]/.test(value)
        ? {
            forbiddenCharacters:
              'Forbidden characters found. Allowed characters are: A-Za-z0-9.,!@#$%^&*?(){}[]|_<>',
          }
        : null;
    };
  }

  static minLength(length = 10): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value ?? '';

      return value.length === 0 || value.length >= length
        ? null
        : {
            passwordMinLength: `Password must be at least ${length} characters long`,
          };
    };
  }

  static maxLength(length = 79): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value ?? '';

      return value.length <= length
        ? null
        : {
            passwordMaxLength: `Password must be maximum ${length} characters long`,
          };
    };
  }

  static complexity(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value ?? '';

      if (!value) {
        return null;
      }

      const errors: string[] = [];

      if (!/[a-z]/.test(value)) {
        errors.push('At least 1 lowercase character.');
      }

      if (!/[A-Z]/.test(value)) {
        errors.push('At least 1 uppercase character.');
      }

      if (!/\d/.test(value)) {
        errors.push('At least 1 numeric character.');
      }

      if (!/[.,!@#$%^&*?(){}[\]|_<>]/.test(value)) {
        errors.push('At least 1 special character (.,!@#$%^&*?(){}[]|_<>).');
      }

      if (value.includes('\\')) {
        errors.push('Password should not include \\.');
      }

      return errors.length
        ? {
            passwordComplexity:
              'Password does not match the following criteria: ' + errors.join(' '),
          }
        : null;
    };
  }

  static match(
    passwordControlName = 'password',
    confirmPasswordControlName = 'confirmPassword',
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const form = control as FormGroup;

      const password = form.get(passwordControlName);
      const confirmPassword = form.get(confirmPasswordControlName);

      if (!password || !confirmPassword) {
        return null;
      }

      // Remove our previous error while preserving other errors.
      if (confirmPassword.errors?.['passwordMismatch']) {
        const errors = { ...confirmPassword.errors };
        delete errors['passwordMismatch'];

        confirmPassword.setErrors(Object.keys(errors).length ? errors : null);
      }

      if (confirmPassword.value && password.value !== confirmPassword.value) {
        confirmPassword.setErrors({
          ...(confirmPassword.errors ?? {}),
          passwordMismatch: 'Passwords do not match',
        });
      }

      return null;
    };
  }
}
